import axios from 'axios';
import { logger } from './logger';
import { listRefreshEvents } from './listRefreshEvents';
import { isCriticalRequest } from '../config/criticalEndpoints';
import { classifyError, ErrorType } from '../utils/errorClassification';
import { getErrorDetails } from '../utils/errorParser';
import { showErrorToast } from '../utils/errorToast';
import { resolveDisplayPhotoUrl } from '../utils/photoUrl';
// httpError only reaches back into ../utils/errorParser, never into this module, so this adds
// no import cycle.
import { okJson } from './httpError';
import { normalizePhotoForUpload } from '../utils/uploadPhoto';

// Use Kong as the gRPC gateway - all requests go through Kong using gRPC-Web
// Empty string = on-premise mode: requests go to same origin, nginx proxies /api → condo-manager
const KONG_HTTP_URL = process.env.REACT_APP_KONG_HTTP_URL ?? 'https://access.panther.com.br';

// Use relative URL for condo-manager backend when behind Traefik
// This allows the app to work at any domain/path (e.g., localhost:9080/web)
const CONDO_MANAGER_URL = process.env.REACT_APP_CONDO_MANAGER_URL ?? 'https://access.panther.com.br';

// Debug: Log the actual values being used
console.log(' [API Config]', {
  REACT_APP_CONDO_MANAGER_URL: process.env.REACT_APP_CONDO_MANAGER_URL,
  CONDO_MANAGER_URL,
  REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
});

/**
 * Get the base URL for API/image requests.
 * Used to convert relative URLs (like /api/v1/images/...) to absolute URLs.
 * In production, REACT_APP_CONDO_MANAGER_URL should be set.
 * In development, falls back to empty string (requests go to same origin).
 */
export const getApiBaseUrl = (): string => {
  return CONDO_MANAGER_URL;
};

/**
 * Convert a photo/image URL into the URL that should actually be displayed.
 *
 * Relative paths get the configured API base URL prepended, as before.
 *
 * S3 URLs are additionally rewritten to condo-manager's stable image proxy. The
 * backend hands out presigned URLs that expire after 15 minutes, which breaks images
 * on long-lived screens: the operational view holds those URLs in state, and after
 * sitting idle past the expiry every re-render paints a broken photo. The proxy path
 * carries no signature and never expires, so this rewrite removes the failure mode
 * rather than retrying it — and costs no extra requests, which is what keeps a screen
 * full of simultaneously-expiring faces from turning into a request storm.
 *
 * See src/utils/photoUrl.ts for the parsing rules and the reasoning in full.
 */
export const makeAbsolutePhotoUrl = (photoUrl: string | null | undefined): string | null => {
  return resolveDisplayPhotoUrl(photoUrl, (relative) => {
    const baseUrl = getApiBaseUrl();
    return baseUrl ? `${baseUrl}${relative}` : relative;
  });
};

// Use Traefik proxy through relative paths
const API_URL = CONDO_MANAGER_URL;
const AUTH_API_URL = KONG_HTTP_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authApi = axios.create({
  baseURL: `${AUTH_API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management with refresh lock to prevent concurrent refresh attempts
let keycloakToken: string | undefined;
let tokenRefreshCallback: (() => Promise<boolean>) | null = null;
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const setApiToken = (token: string | undefined) => {
  keycloakToken = token;
  if (token) {
    localStorage.setItem('token', token);
  }
};

export const setTokenRefreshCallback = (callback: () => Promise<boolean>) => {
  tokenRefreshCallback = callback;
};

// Get current token from memory or localStorage
const getToken = (): string | undefined => {
  return keycloakToken || localStorage.getItem('token') || undefined;
};

// Exported version — use after refreshToken() to get the freshest token
export const getCurrentToken = (): string | undefined => getToken();

// Refresh token with mutex to prevent concurrent refreshes
const refreshToken = async (): Promise<boolean> => {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    logger.debug('api.auth', 'Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  // If no refresh callback, can't refresh
  if (!tokenRefreshCallback) {
    logger.warn('api.auth', 'No token refresh callback available');
    return false;
  }

  // Start refreshing
  isRefreshing = true;
  logger.info('api.auth', 'Starting token refresh');

  refreshPromise = tokenRefreshCallback()
    .then((success) => {
      if (success) {
        logger.info('api.auth', 'Token refresh successful');
      } else {
        logger.warn('api.auth', 'Token refresh failed');
      }
      return success;
    })
    .catch((error) => {
      logger.error('api.auth', 'Token refresh error', error);
      return false;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

// Tracks whether a deploy-related outage is currently active.
// Events are only dispatched on state transitions (off→on and on→off), not on every request.
let _deployingActive = false;

const notifyDeploying = () => {
  if (!_deployingActive) {
    _deployingActive = true;
    window.dispatchEvent(new CustomEvent('panther:deploying'));
  }
};

const notifyDeployOk = () => {
  if (_deployingActive) {
    _deployingActive = false;
    window.dispatchEvent(new CustomEvent('panther:deploy-ok'));
  }
};

// Actual fetch implementation with auth header + 401 refresh-retry. Not exported —
// GET callers must go through fetchWithAuth below, which de-dupes concurrent identical
// GETs before they ever reach here.
const executeFetchWithAuth = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const token = getToken();

  // Add Authorization header
  const headers = new Headers(init?.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const requestInit: RequestInit = {
    ...init,
    headers,
  };

  // Resolve the URL - prepend API base URL for relative paths
  let resolvedUrl: string;
  if (typeof input === 'string') {
    if (input.startsWith('/')) {
      // Relative URL - prepend the API base URL
      resolvedUrl = `${CONDO_MANAGER_URL}${input}`;
    } else if (!input.startsWith('http://') && !input.startsWith('https://')) {
      // Relative URL without leading slash - prepend the API base URL with slash
      resolvedUrl = `${CONDO_MANAGER_URL}/${input}`;
    } else {
      // Absolute URL - use as is
      resolvedUrl = input;
    }
  } else if (input instanceof URL) {
    resolvedUrl = input.toString();
  } else {
    // RequestInfo (Request object) - use as is
    resolvedUrl = (input as Request).url;
  }

  // Debug: Log URL resolution
  console.log(' [fetchWithAuth]', {
    originalInput: typeof input === 'string' ? input : 'non-string',
    resolvedUrl,
    CONDO_MANAGER_URL,
  });

  logger.debug('api.fetchWithAuth', 'Making fetch request', {
    originalInput: typeof input === 'string' ? input : 'non-string',
    resolvedUrl,
    baseUrl: CONDO_MANAGER_URL,
  });

  // First attempt
  let response = await fetch(resolvedUrl, requestInit);

  // 502/503 = ECS rolling deploy in progress — show friendly banner
  // Exception: guarita endpoints intentionally return 502 when the physical device
  // is unreachable — that is NOT a deploy event, so don't show the banner.
  const isGuaritaPath = resolvedUrl.includes('/guarita/');
  if ((response.status === 502 || response.status === 503) && !isGuaritaPath) {
    notifyDeploying();
  } else if (response.ok) {
    notifyDeployOk();
  }

  // If 401, try to refresh and retry
  if (response.status === 401) {
    logger.info('api.fetch', 'Got 401, attempting token refresh');

    const refreshed = await refreshToken();

    if (refreshed) {
      const newToken = getToken();
      if (newToken) {
        // Build a fresh Headers for the retry rather than mutating the instance the
        // first request was issued with. Reusing it made the two attempts share one
        // mutable object, so the original request's headers appeared to change after
        // the fact — which hides what was actually sent and couples the retry to a
        // value the caller may still hold a reference to.
        const retryHeaders = new Headers(init?.headers || {});
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
        const retryInit: RequestInit = {
          ...init,
          headers: retryHeaders,
        };

        logger.debug('api.fetch', 'Token refreshed, retrying request');
        response = await fetch(resolvedUrl, retryInit);
      }
    }
  }

  return response;
};

// In-flight de-duplication for GET requests: if two independent callers request the exact
// same URL while a request is already in progress, they share one network call (including
// the 401-refresh-retry above) instead of each firing their own. This generalizes a fix
// first applied per-endpoint in useCustomerDevicesAndZones.ts after Layout and
// OperationalView were independently fetching the same customer's zones/devices.
// Mutating requests (POST/PUT/DELETE) are never de-duped — only exact-match GETs.
const inflightGets = new Map<string, Promise<Response>>();

const resolveDedupeKey = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return (input as Request).url;
};

// Custom fetch wrapper that automatically retries with token refresh on 401
export const fetchWithAuth = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const method = (init?.method || 'GET').toUpperCase();
  if (method !== 'GET') {
    return executeFetchWithAuth(input, init);
  }

  const key = resolveDedupeKey(input);
  const existing = inflightGets.get(key);
  if (existing) {
    const response = await existing;
    return response.clone();
  }

  const promise = executeFetchWithAuth(input, init);
  inflightGets.set(key, promise);
  try {
    const response = await promise;
    return response.clone();
  } finally {
    inflightGets.delete(key);
  }
};

// Test-only escape hatch — see useCustomerDevicesAndZones.ts for why this class of module
// state needs clearing between test cases that reuse the same URL with fresh mocks.
export const __resetFetchDedupeCacheForTests = () => {
  inflightGets.clear();
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get current token using centralized getter
    const token = getToken();
    logger.debug('api.interceptor.request', 'API request initiated', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      tokenAvailable: !!token,
      tokenLength: token?.length,
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      logger.warn('api.interceptor.request', 'No token available for request', {
        url: config.url,
      });
    }
    return config;
  },
  (error) => {
    logger.error('api.interceptor.request', 'Request interceptor error', error, {
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

authApi.interceptors.request.use(
  (config) => {
    // Get current token using centralized getter
    const token = getToken();
    logger.debug('authApi.interceptor.request', 'Auth API request initiated', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      tokenAvailable: !!token,
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logger.error('authApi.interceptor.request', 'Auth request interceptor error', error, {
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors with automatic token refresh
api.interceptors.response.use(
  (response) => {
    logger.debug('api.interceptor.response', 'API request succeeded', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
    });
    notifyDeployOk();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 502/503 = ECS rolling deploy in progress — show banner and suppress error toast
    // Exception: guarita endpoints intentionally return 502 when the physical device
    // is unreachable — that is NOT a deploy event, so don't show the banner.
    const httpStatus = error.response?.status;
    const reqUrl: string = originalRequest?.url ?? '';
    if ((httpStatus === 502 || httpStatus === 503) && !reqUrl.includes('/guarita/')) {
      notifyDeploying();
      return Promise.reject(error); // skip all further error handling (no toast)
    }

    // If 401 error and we haven't already retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      logger.warn('api.interceptor.response', 'Got 401, attempting token refresh and retry', {
        url: originalRequest?.url,
      });

      try {
        // Attempt to refresh the token using centralized mutex-protected function
        const refreshed = await refreshToken();

        if (refreshed) {
          // Update the authorization header with the new token
          const newToken = getToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            logger.info('api.interceptor.response', 'Token refreshed successfully, retrying request', {
              url: originalRequest.url,
            });
            // Retry the original request with the new token
            return api(originalRequest);
          }
        }

        logger.error('api.interceptor.response', 'Token refresh failed, rejecting request');

        // Check if this is a critical endpoint - if so, show error overlay
        const criticalConfig = isCriticalRequest(originalRequest);
        if (criticalConfig) {
          window.dispatchEvent(new CustomEvent('showCriticalError', {
            detail: {
              type: ErrorType.AUTH_ERROR,
              message: criticalConfig.errorMessages.authError,
              requestConfig: originalRequest,
              endpoint: criticalConfig
            }
          }));
        } else {
          // Non-critical: session is gone — signal the banner to show login prompt
          // instead of letting the raw "Token validation failed" backend message
          // reach showErrorToast.
          window.dispatchEvent(new CustomEvent('panther:session-expired'));
        }
        return Promise.reject(error);
      } catch (refreshError) {
        logger.error('api.interceptor.response', 'Error during token refresh', refreshError);

        // Check if this is a critical endpoint - if so, show error overlay
        const criticalConfig = isCriticalRequest(originalRequest);
        if (criticalConfig) {
          window.dispatchEvent(new CustomEvent('showCriticalError', {
            detail: {
              type: ErrorType.AUTH_ERROR,
              message: criticalConfig.errorMessages.authError,
              requestConfig: originalRequest,
              endpoint: criticalConfig
            }
          }));
        }

        return Promise.reject(error);
      }
    }

    // Silence 403 on access-logs — porteiro users are intentionally restricted, no toast needed
    if (error.response?.status === 403 && error.config?.url?.includes('/access-logs')) {
      return Promise.reject(error);
    }

    // Check if this is a schedule-related 403 error (access denied due to time restrictions)
    if (error.response?.status === 403 && error.response?.data?.error_code) {
      const errorCode = error.response.data.error_code;
      const currentTime = error.response.data.current_time;

      // Schedule-related error codes
      const scheduleErrors = ['outside_allowed_hours', 'no_active_assignments', 'assignments_not_active'];

      if (scheduleErrors.includes(errorCode)) {
        logger.warn('api.error', 'Access denied due to schedule restriction', {
          errorCode,
          currentTime,
          username: error.response.data.username,
        });

        // Dispatch event to show access denied overlay
        window.dispatchEvent(new CustomEvent('showAccessDenied', {
          detail: {
            reason: errorCode,
            currentTime: currentTime,
          }
        }));

        return Promise.reject(error);
      }
    }

    // Get detailed error information using new error parser
    const errorDetails = getErrorDetails(error);

    // Log error with error code
    logger.error('api.error', 'API request failed', {
      errorCode: errorDetails.code,
      errorType: errorDetails.type,
      errorDomain: errorDetails.domain,
      message: errorDetails.message,
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });

    // Check if this is a critical endpoint that failed
    const criticalConfig = isCriticalRequest(originalRequest);
    if (criticalConfig) {
      const errorType = classifyError(error);

      // Get appropriate error message based on error type
      const message = (() => {
        switch (errorType) {
          case ErrorType.SERVER_ERROR:
            return criticalConfig.errorMessages.serverError;
          case ErrorType.NETWORK_ERROR:
            return criticalConfig.errorMessages.networkError;
          case ErrorType.AUTH_ERROR:
            return criticalConfig.errorMessages.authError;
          default:
            return 'Erro inesperado. Tente novamente.';
        }
      })();

      // Dispatch event to show full-screen error overlay
      window.dispatchEvent(new CustomEvent('showCriticalError', {
        detail: {
          type: errorType,
          message,
          requestConfig: originalRequest,
          endpoint: criticalConfig
        }
      }));
    } else if (!(error.config as any)?._silentOnError) {
      // For non-critical endpoints, show error toast (unless caller opted out)
      showErrorToast(error);
    }
    return Promise.reject(error);
  }
);

authApi.interceptors.response.use(
  (response) => {
    logger.debug('authApi.interceptor.response', 'Auth API request succeeded', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 error and we haven't already retried this request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      logger.warn('authApi.interceptor.response', 'Got 401, attempting token refresh and retry', {
        url: originalRequest?.url,
      });

      try {
        // Attempt to refresh the token using centralized mutex-protected function
        const refreshed = await refreshToken();

        if (refreshed) {
          // Update the authorization header with the new token
          const newToken = getToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            logger.info('authApi.interceptor.response', 'Token refreshed successfully, retrying request', {
              url: originalRequest.url,
            });
            // Retry the original request with the new token
            return authApi(originalRequest);
          }
        }

        logger.error('authApi.interceptor.response', 'Token refresh failed, rejecting request');

        // Check if this is a critical endpoint - if so, show error overlay
        const criticalConfig = isCriticalRequest(originalRequest);
        if (criticalConfig) {
          window.dispatchEvent(new CustomEvent('showCriticalError', {
            detail: {
              type: ErrorType.AUTH_ERROR,
              message: criticalConfig.errorMessages.authError,
              requestConfig: originalRequest,
              endpoint: criticalConfig
            }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('panther:session-expired'));
        }
        return Promise.reject(error);
      } catch (refreshError) {
        logger.error('authApi.interceptor.response', 'Error during token refresh', refreshError);

        // Check if this is a critical endpoint - if so, show error overlay
        const criticalConfig = isCriticalRequest(originalRequest);
        if (criticalConfig) {
          window.dispatchEvent(new CustomEvent('showCriticalError', {
            detail: {
              type: ErrorType.AUTH_ERROR,
              message: criticalConfig.errorMessages.authError,
              requestConfig: originalRequest,
              endpoint: criticalConfig
            }
          }));
        }

        return Promise.reject(error);
      }
    }

    // Get detailed error information using new error parser
    const errorDetails = getErrorDetails(error);

    // Log error with error code
    logger.error('authApi.error', 'Auth API request failed', {
      errorCode: errorDetails.code,
      errorType: errorDetails.type,
      errorDomain: errorDetails.domain,
      message: errorDetails.message,
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });

    // Check if this is a critical endpoint that failed
    const criticalConfig = isCriticalRequest(originalRequest);
    if (criticalConfig) {
      const errorType = classifyError(error);

      // Get appropriate error message based on error type
      const message = (() => {
        switch (errorType) {
          case ErrorType.SERVER_ERROR:
            return criticalConfig.errorMessages.serverError;
          case ErrorType.NETWORK_ERROR:
            return criticalConfig.errorMessages.networkError;
          case ErrorType.AUTH_ERROR:
            return criticalConfig.errorMessages.authError;
          default:
            return 'Erro inesperado. Tente novamente.';
        }
      })();

      // Dispatch event to show full-screen error overlay
      window.dispatchEvent(new CustomEvent('showCriticalError', {
        detail: {
          type: errorType,
          message,
          requestConfig: originalRequest,
          endpoint: criticalConfig
        }
      }));
    } else if (!(error.config as any)?._silentOnError) {
      // For non-critical endpoints, show error toast (unless caller opted out)
      showErrorToast(error);
    }
    return Promise.reject(error);
  }
);

// In-flight de-duplication for GET requests on the `api` axios instance — same rationale
// as fetchWithAuth's dedup above (see there for the fuller explanation). Axios responses
// are already-parsed data (unlike fetch's one-read-only body), so callers can safely share
// the same resolved value directly — no cloning needed.
const inflightAxiosGets = new Map<string, ReturnType<typeof api.get>>();

const originalApiGet = api.get.bind(api);
api.get = ((url: string, config?: Parameters<typeof api.get>[1]) => {
  const key = `${url}?${JSON.stringify(config?.params ?? {})}`;
  const existing = inflightAxiosGets.get(key);
  if (existing) return existing;

  const promise = originalApiGet(url, config);
  inflightAxiosGets.set(key, promise);
  // .finally() returns a NEW derived promise that rethrows on rejection — if left
  // dangling with no handler, that derived promise (not the one returned to callers
  // below) surfaces as an uncaught runtime error on any non-2xx response. The actual
  // `promise` returned to callers is untouched by this and rejects normally for them.
  promise.finally(() => inflightAxiosGets.delete(key)).catch(() => {});
  return promise;
}) as typeof api.get;

// Test-only escape hatch — see useCustomerDevicesAndZones.ts for why this class of module
// state needs clearing between test cases that reuse the same URL with fresh mocks.
export const __resetApiGetDedupeCacheForTests = () => {
  inflightAxiosGets.clear();
};

// Pet interface
export interface Pet {
  id: string;
  customer_id: string;
  unit_id?: string;
  name: string;
  breed?: string;
  description?: string;
  photo_url?: string;
  photo_url_2?: string;
  photo_url_3?: string;
  photo_url_4?: string;
  species?: string;
  size?: string;
  weight?: number;
  sex?: string;
  color?: string;
  castrated?: boolean;
  vaccines?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  unit?: {
    id: string;
    number: string;
    block?: { id: string; name: string };
    street?: { id: string; name: string };
    street_number?: string;
  };
}

// UnitDocument interface
export interface UnitDocument {
  id: string;
  unit_id: string;
  customer_id: string;
  name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  created_at: string;
  updated_at: string;
}

// Obra (Construction Works) interface
export interface ObraAttachment {
  name: string;
  url: string;
  type: 'art' | 'rrt' | 'outros';
  size: number;
  uploaded_at: string;
}

export type ObraStatus =
  | 'aguardando_aprovacao'
  | 'aprovada'
  | 'em_andamento'
  | 'concluida'
  | 'suspensa'
  | 'reprovada';

export interface Obra {
  id: string;
  customer_id: string;
  unit_id?: string;
  title: string;
  local?: string;
  description?: string;
  status: ObraStatus;
  start_date?: string;
  duration_days: number;
  generates_noise: boolean;
  company_name?: string;
  company_cnpj?: string;
  company_email?: string;
  company_phone?: string;
  tech_name?: string;
  tech_cpf?: string;
  tech_email?: string;
  tech_phone?: string;
  tech_profession?: string;
  tech_crea?: string;
  attachments: ObraAttachment[];
  created_at: string;
  updated_at: string;
  unit?: { id: string; name: string; number?: string };
}

// TimeZone interface
export interface TimeZone {
  id: string;
  customer_id: string;
  name: string;
  description?: string;
  schedule: Record<string, { enabled: boolean; start?: string; end?: string }>;
  unit_scope: 'all' | 'specific_units' | 'specific_blocks';
  unit_ids: string[];
  block_ids: string[];
  department_scope: 'all' | 'specific';
  department_ids: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// API parameter interfaces
export interface GetPeopleParams {
  role_type?: string;
  is_active?: string | boolean;
  search?: string;
  include_deleted?: boolean;
  include_inactive_roles?: boolean;
  created_today?: boolean;
  created_date?: string;
  start_date?: string;
  end_date?: string;
  visitor_state?: string;          // Filter by visitor_state in role_metadata (e.g. "inside,awaiting_entry")
  visited_today?: boolean;         // Filter by first_entry_at >= today start
  unit_id?: string;
  department_id?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
  limit?: number;
  // Advanced filters
  has_keycloak?: boolean;          // Filter by Keycloak account presence
  min_age?: number;                // Minimum age (requires date_of_birth)
  max_age?: number;                // Maximum age (requires date_of_birth)
  block_id?: string;               // Filter residents by block UUID
  relation_type?: string;          // "proprietario"|"inquilino"|"dependente"|"familiar"
  authorization_status?: 'authorized' | 'pending';  // Filter by role authorization
  service_type?: string;           // Filter service providers by service type
}

// API endpoints
export const condoApi = {
  // Customers (All types) - Unified endpoints
  getCustomers: () => api.get('/customers'),
  getCustomer: (id: string) => api.get(`/customers/${id}`),
  createCustomer: (data: any) => api.post('/customers', data),
  updateCustomer: (id: string, data: any) => api.put(`/customers/${id}`, data),
  // Despite the verb, this only disables the customer (is_active = false) — see
  // CustomerHandler.DeleteCustomer. Deletion is blocked server-side.
  deleteCustomer: (id: string) => api.delete(`/customers/${id}`),
  setCustomerActive: (id: string, isActive: boolean) =>
    api.patch(`/customers/${id}/active`, { is_active: isActive }),
  getInactiveCustomers: () => api.get(`/customers`, { params: { status: 'inactive' } }),

  // Condos
  getCondos: () => api.get('/condos'),
  getCondo: (id: string) => api.get(`/condos/${id}`),
  createCondo: (data: any) => api.post('/condos', data),
  updateCondo: (id: string, data: any) => api.put(`/condos/${id}`, data),
  deleteCondo: (id: string) => api.delete(`/condos/${id}`),

  // Factories
  getFactories: () => api.get('/factories'),
  getFactory: (id: string) => api.get(`/factories/${id}`),
  createFactory: (data: any) => api.post('/factories', data),
  updateFactory: (id: string, data: any) => api.put(`/factories/${id}`, data),
  deleteFactory: (id: string) => api.delete(`/factories/${id}`),

  // Companies
  getCompanies: () => api.get('/companies'),
  getCompany: (id: string) => api.get(`/companies/${id}`),
  createCompany: (data: any) => api.post('/companies', data),
  updateCompany: (id: string, data: any) => api.put(`/companies/${id}`, data),
  deleteCompany: (id: string) => api.delete(`/companies/${id}`),
  
  // Condo Setup
  setupCondoStructure: (id: string, data: any) => api.post(`/condos/${id}/setup`, data),
  getCondoStructure: (id: string) => api.get(`/condos/${id}/structure`),
  validateCondoSetup: (id: string, data: any) => api.post(`/condos/${id}/validate-setup`, data),

  // Blocks
  getBlocks: (condoId: string) => api.get(`/condos/${condoId}/blocks`),
  getBlock: (id: string) => api.get(`/blocks/${id}`),
  createBlock: (data: any) => api.post('/blocks', data),
  updateBlock: (id: string, data: any) => api.put(`/blocks/${id}`, data),
  deleteBlock: (id: string) => api.delete(`/blocks/${id}`),

  // Streets
  getStreets: (customerId: string) => api.get(`/customers/${customerId}/streets`),
  createStreet: (customerId: string, data: { name: string }) => api.post(`/customers/${customerId}/streets`, data),
  updateStreet: (id: string, data: { name: string }) => api.put(`/streets/${id}`, data),
  deleteStreet: (id: string) => api.delete(`/streets/${id}`),

  // Units
  getUnits: (blockId: string) => api.get(`/blocks/${blockId}/units`),
  getUnit: (id: string) => api.get(`/units/${id}`),
  createUnit: (data: any) => api.post('/units', data),
  updateUnit: (id: string, data: any) => api.put(`/units/${id}`, data),
  deleteUnit: (id: string) => api.delete(`/units/${id}`),
  searchUnits: (customerId: string, search: string, params?: any) =>
    api.get(`/customers/${customerId}/units/search`, {
      params: { search, ...params }
    }),

  // Residents
  getResidents: (unitId?: string) => api.get('/residents', { params: { unitId } }),
  getResident: (id: string) => api.get(`/residents/${id}`),
  createResident: (data: any) => api.post('/residents', data),
  updateResident: (id: string, data: any) => api.put(`/residents/${id}`, data),
  deleteResident: (id: string) => api.delete(`/residents/${id}`),

  // Unified People Management (New Architecture)
  getPeople: (customerId: string, params?: GetPeopleParams) => api.get(`/customers/${customerId}/people`, { params }),
  getPerson: (customerId: string, personId: string) => api.get(`/customers/${customerId}/people/${personId}`),
  searchPersonByCPF: (customerId: string, cpf: string) => api.get(`/customers/${customerId}/people/search/cpf/${cpf}`),
  createPerson: async (customerId: string, data: any) => {
    const response = await api.post(`/customers/${customerId}/people`, data);
    listRefreshEvents.emit('people');
    return response;
  },
  updatePerson: async (customerId: string, personId: string, data: any) => {
    const response = await api.put(`/customers/${customerId}/people/${personId}`, data);
    listRefreshEvents.emit('people');
    return response;
  },
  deletePerson: async (customerId: string, personId: string) => {
    const response = await api.delete(`/customers/${customerId}/people/${personId}`);
    listRefreshEvents.emit('people');
    return response;
  },

  // Person Role Management
  getPersonRoles: (customerId: string, personId: string) => api.get(`/customers/${customerId}/people/${personId}/roles`),
  createPersonRole: async (customerId: string, personId: string, data: any) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles`, data);
    listRefreshEvents.emit('people');
    return response;
  },
  updatePersonRole: async (customerId: string, personId: string, roleId: string, data: any) => {
    const response = await api.put(`/customers/${customerId}/people/${personId}/roles/${roleId}`, data);
    listRefreshEvents.emit('people');
    return response;
  },
  deletePersonRole: async (customerId: string, personId: string, roleId: string) => {
    const response = await api.delete(`/customers/${customerId}/people/${personId}/roles/${roleId}`);
    listRefreshEvents.emit('people');
    return response;
  },
  authorizePersonRole: async (customerId: string, personId: string, roleId: string) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles/${roleId}/authorize`);
    listRefreshEvents.emit('people');
    return response;
  },
  revokePersonRoleAuthorization: async (customerId: string, personId: string, roleId: string, reason?: string) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles/${roleId}/revoke-authorization`,
      reason ? { reason } : undefined
    );
    listRefreshEvents.emit('people');
    return response;
  },
  manualCheckIn: async (customerId: string, personId: string, roleId: string, localRemovalDone = false) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles/${roleId}/check-in`,
      localRemovalDone ? { local_removal_done: true } : undefined
    );
    listRefreshEvents.emit('visitors');
    return response;
  },
  manualCheckOut: async (customerId: string, personId: string, roleId: string, localRemovalDone = false) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles/${roleId}/check-out`,
      localRemovalDone ? { local_removal_done: true } : undefined
    );
    listRefreshEvents.emit('visitors');
    return response;
  },
  autoCheckIn: async (customerId: string, personId: string, roleId: string, localRemovalDone = false) => {
    const response = await api.post(
      `/customers/${customerId}/people/${personId}/roles/${roleId}/check-in`,
      { source: 'auto', ...(localRemovalDone && { local_removal_done: true }) },
      { _silentOnError: true } as any
    );
    listRefreshEvents.emit('visitors');
    return response;
  },
  autoCheckOut: async (customerId: string, personId: string, roleId: string, localRemovalDone = false) => {
    const response = await api.post(
      `/customers/${customerId}/people/${personId}/roles/${roleId}/check-out`,
      { source: 'auto', ...(localRemovalDone && { local_removal_done: true }) },
      { _silentOnError: true } as any
    );
    listRefreshEvents.emit('visitors');
    return response;
  },
  /**
   * `payload` describes HOW the person came in — on foot, in a car with a plate, in a rideshare
   * (see utils/manualAccessMode). Optional and ignored by the current backend, which takes no body
   * on this route: sending it now means that the day the handler starts reading it, clients already
   * in the field are already providing it.
   */
  registerManualEntry: async (customerId: string, personId: string, roleId: string, payload?: Record<string, unknown>) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles/${roleId}/manual-entry`, payload);
    listRefreshEvents.emit('people');
    return response;
  },
  registerManualExit: async (customerId: string, personId: string, roleId: string, payload?: Record<string, unknown>) => {
    const response = await api.post(`/customers/${customerId}/people/${personId}/roles/${roleId}/manual-exit`, payload);
    listRefreshEvents.emit('people');
    return response;
  },
  getPersonHistory: (customerId: string, personId: string) => api.get(`/customers/${customerId}/people/${personId}/history`),
  syncPerson: (customerId: string, personId: string) => api.post(`/customers/${customerId}/people/${personId}/sync`),

  // Visitors (Legacy - kept for backward compatibility during migration)
  getVisitors: (residentId?: string) => api.get('/visitors', { params: { residentId } }),
  getVisitor: (id: string) => api.get(`/visitors/${id}`),
  createVisitor: (data: any) => api.post('/visitors', data),
  updateVisitor: (id: string, data: any) => api.put(`/visitors/${id}`, data),
  deleteVisitor: (id: string) => api.delete(`/visitors/${id}`),

  // Visitor Statistics
  getPersonVisitorStats: async (customerId: string, personId: string): Promise<{
    last_visit_date?: string;
    total_visits: number;
    current_status: 'active' | 'inactive' | 'never_visited';
    active_visit?: {
      id: string;
      check_in_time: string;
      unit_number?: string;
      block_name?: string;
      access_expires_at?: string;
      valid_until?: string;
    };
  }> => {
    const response = await api.get(`/customers/${customerId}/people/${personId}/visitor-stats`);
    return response.data?.data || response.data;
  },

  // Staff
  getStaff: (condoId?: string) => api.get('/staff', { params: { condoId } }),
  getStaffMember: (id: string) => api.get(`/staff/${id}`),
  createStaff: (data: any) => api.post('/staff', data),
  updateStaff: (id: string, data: any) => api.put(`/staff/${id}`, data),
  deleteStaff: (id: string) => api.delete(`/staff/${id}`),

  // Roles and Permissions
  getRoles: () => api.get('/roles'),
  getRole: (id: string) => api.get(`/roles/${id}`),
  createRole: (data: any) => api.post('/roles', data),
  updateRole: (id: string, data: any) => api.put(`/roles/${id}`, data),
  deleteRole: (id: string) => api.delete(`/roles/${id}`),
  getPermissions: () => api.get('/permissions'),
  assignRole: (userId: string, condoId: string, roleId: string) => 
    api.post('/user-assignments', { userId, condoId, roleId }),

  // Access Logs
  getAccessLogs: (params?: any) => api.get('/access-logs', { params }),

  // Dashboard
  getDashboardStats: (customerId: string) => api.get('/dashboard/stats', { params: { customer_id: customerId } }),
  getAdminDashboardSummary: (customerId: string) => api.get(`/dashboard/admin-summary/${customerId}`),
  getGlobalDashboardStats: () => api.get('/dashboard/global-summary'),

  // User Management (from auth service)
  getUsers: (params?: any) => authApi.get('/users', { params }),
  getUser: (id: string) => authApi.get(`/users/${id}`),
  createUser: (data: any) => authApi.post('/users', data),
  updateUser: (id: string, data: any) => authApi.put(`/users/${id}`, data),
  deleteUser: (id: string) => authApi.delete(`/users/${id}`),

  // User Assignments
  getUserAssignments: (params?: any) => api.get('/user-assignments', { params }),
  getUserAssignment: (id: string) => api.get(`/user-assignments/${id}`),
  updateUserAssignment: (id: string, data: any) => api.put(`/user-assignments/${id}`, data),
  deleteUserAssignment: (id: string) => api.delete(`/user-assignments/${id}`),

  // Departments (Empresa)
  getDepartments: (customerId: string) => api.get(`/customers/${customerId}/departments`),
  getDepartment: (customerId: string, departmentId: string) => api.get(`/customers/${customerId}/departments/${departmentId}`),
  createDepartment: (customerId: string, data: any) => api.post(`/customers/${customerId}/departments`, data),
  updateDepartment: (customerId: string, departmentId: string, data: any) => api.put(`/customers/${customerId}/departments/${departmentId}`, data),
  deleteDepartment: (customerId: string, departmentId: string) => api.delete(`/customers/${customerId}/departments/${departmentId}`),
  getDepartmentEmployees: (customerId: string, departmentId: string) => api.get(`/customers/${customerId}/departments/${departmentId}/employees`),

  // Department Members Management
  getDepartmentMembers: (customerId: string, departmentId: string) => api.get(`/customers/${customerId}/departments/${departmentId}/members`),
  addDepartmentMember: (customerId: string, departmentId: string, personId: string) => api.post(`/customers/${customerId}/departments/${departmentId}/members`, { person_id: personId }),
  removeDepartmentMember: (customerId: string, departmentId: string, personId: string) => api.delete(`/customers/${customerId}/departments/${departmentId}/members/${personId}`),

  // Employees (Funcionarios)
  getEmployees: (customerId: string, params?: any) => api.get(`/customers/${customerId}/employees`, { params }),
  createEmployee: (customerId: string, data: any) => api.post(`/customers/${customerId}/employees`, data),
  assignEmployeeToDepartment: (employeeId: string, departmentId: string) => api.post(`/employees/${employeeId}/department/${departmentId}`),

  // Global Search
  globalSearch: (params: {
    q: string;
    types?: string[];
    limit?: number;
    customerId?: string;
  }) => api.get('/search', { params: {
    q: params.q,
    types: params.types?.join(','),
    limit: params.limit || 20,
    customer_id: params.customerId
  }}),

  // Customer Notes (Operator notes/alerts for operacional view)
  // Supports hierarchical filtering: person_id for person-specific, unit_id for condominios, department_id for empresas
  // If none is provided, returns only global notes (customer-level)
  getCustomerNotes: (customerId: string, active: boolean = true, filters?: { person_id?: string; unit_id?: string; department_id?: string }, noteType?: string) =>
    api.get(`/customers/${customerId}/notes`, { params: { active, ...filters, ...(noteType ? { note_type: noteType } : {}) } }),
  getCustomerNotesHistory: (customerId: string, params?: { page?: number; limit?: number; status?: string; scope?: string; person_id?: string; unit_id?: string; department_id?: string; note_type?: string }) =>
    api.get(`/customers/${customerId}/notes/history`, { params }),
  createCustomerNote: (customerId: string, content: string, scope?: { person_id?: string; unit_id?: string; department_id?: string }, noteType?: string, photoUrl?: string) =>
    api.post(`/customers/${customerId}/notes`, { content, ...scope, ...(noteType ? { note_type: noteType } : {}), ...(photoUrl ? { photo_url: photoUrl } : {}) }),
  uploadCustomerNotePhoto: (customerId: string, formData: FormData) =>
    api.post(`/customers/${customerId}/notes/photo`, formData),
  concludeCustomerNote: (customerId: string, noteId: string, reason?: string) =>
    api.put(`/customers/${customerId}/notes/${noteId}/conclude`, { reason }),
  // A livro page is a thread: follow-ups, the conclusion that says what happened, and a reopening
  // when something comes back. The apps have written these for a while; the web could only read
  // the note itself, so a conclusion recorded on a phone was invisible at the desk.
  getCustomerNoteEntries: (customerId: string, noteId: string) =>
    api.get(`/customers/${customerId}/notes/${noteId}/entries`),
  addCustomerNoteEntry: (customerId: string, noteId: string, body: string) =>
    api.post(`/customers/${customerId}/notes/${noteId}/entries`, { body }),
  reopenCustomerNote: (customerId: string, noteId: string, reason: string) =>
    api.post(`/customers/${customerId}/notes/${noteId}/reopen`, { reason }),
  getLivroSummary: (customerId: string) =>
    api.get(`/customers/${customerId}/notes/livro-summary`),

  // Achados e Perdidos — the portaria publishes what it is holding; a resident gets in touch.
  // Gated server-side to sindico/porteiro and above for everything except reading and claiming.
  getLostFoundItems: (customerId: string, params?: { status?: string }) =>
    api.get(`/customers/${customerId}/lost-found`, { params }),
  createLostFoundItem: (customerId: string, body: { title: string; description?: string; found_location?: string; photo_url?: string }) =>
    api.post(`/customers/${customerId}/lost-found`, body),
  uploadLostFoundPhoto: (customerId: string, formData: FormData) =>
    api.post(`/customers/${customerId}/lost-found/photo`, formData),
  resolveLostFoundItem: (customerId: string, itemId: string, status: string) =>
    api.post(`/customers/${customerId}/lost-found/${itemId}/resolve`, { status }),
  deleteLostFoundItem: (customerId: string, itemId: string) =>
    api.delete(`/customers/${customerId}/lost-found/${itemId}`),
  getLostFoundClaims: (customerId: string, itemId: string) =>
    api.get(`/customers/${customerId}/lost-found/${itemId}/claims`),

  // Lembretes (pinned reminder notes for operacional view, shared between all operators)
  getLembretes: (customerId: string) =>
    api.get(`/customers/${customerId}/lembretes`),
  createLembrete: (customerId: string, contentHtml: string = '') =>
    api.post(`/customers/${customerId}/lembretes`, { content_html: contentHtml }),
  updateLembrete: (customerId: string, lembreteId: string, contentHtml: string) =>
    api.put(`/customers/${customerId}/lembretes/${lembreteId}`, { content_html: contentHtml }),
  deleteLembrete: (customerId: string, lembreteId: string) =>
    api.delete(`/customers/${customerId}/lembretes/${lembreteId}`),
  getLembreteHistory: (customerId: string) =>
    api.get(`/customers/${customerId}/lembretes/history`),

  // Customer Settings (configuration management)
  getCustomerSettings: (customerId: string) => api.get(`/customers/${customerId}/settings`),
  updateCustomerSettings: (customerId: string, settings: any) => api.put(`/customers/${customerId}/settings`, settings),
  testSmtpSettings: (customerId: string, to: string) => api.post(`/customers/${customerId}/settings/smtp/test`, { to }),
  getSignupLink: (customerId: string) => api.get(`/customers/${customerId}/signup-link`),

  // Online Access module helpers
  getCustomerIoTDevices: (customerId: string) => api.get(`/iot-devices/customer/${customerId}`),
  // `includeInactive` is for Configurações do Cliente only: a camera deleted while it was still
  // selected for Câmera Online has to stay visible there, by name, so the síndico can untick it.
  getRegistrationCameras: (customerId: string, includeInactive = false) =>
    api.get(`/registration-cameras`, {
      params: { customer_id: customerId, ...(includeInactive ? { include_inactive: 'true' } : {}) },
    }),
  // Rejects on a non-OK response rather than handing back the raw Response. Callers in
  // Customers.tsx wrap this in try/catch to warn "saved, but the logo failed" — with a raw
  // Response those catch blocks were unreachable for HTTP failures, so a rejected upload
  // reported the customer as fully saved.
  uploadCustomerPhoto: async (customerId: string, file: File): Promise<{ photo_url?: string }> => {
    const form = new FormData();
    form.append('photo', await normalizePhotoForUpload(file));
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/photo`, { method: 'POST', body: form });
    return okJson(res, 'Erro ao enviar a foto');
  },

  // Service Providers (Prestadores de Serviço) — time-windowed recurring access
  getServiceProviders: (customerId: string, params?: { status?: string; search?: string; unit_id?: string }) =>
    api.get(`/customers/${customerId}/service-providers`, { params }),
  updateServiceProviderWindow: (
    customerId: string,
    personId: string,
    windowConfig: { entry_window_start?: string; entry_window_end?: string; allowed_days?: string; contractor_id?: string | null; contractor_name?: string | null },
  ) => api.patch(`/customers/${customerId}/service-providers/${personId}/window`, windowConfig),
  getServiceProviderConfig: (customerId: string) =>
    api.get(`/customers/${customerId}/service-provider-config`),
  updateServiceProviderConfig: (
    customerId: string,
    config: { sp_default_window_start?: string; sp_default_window_end?: string; sp_default_allowed_days?: string; sp_default_exit_by?: string; sp_custom_period_enabled?: boolean; sp_custom_period_max_days?: number },
  ) => api.put(`/customers/${customerId}/service-provider-config`, config),

  // SP Contractor companies (Empresas Contratadas)
  listContractors: (customerId: string) =>
    api.get(`/customers/${customerId}/sp-contractors`),
  createContractor: (
    customerId: string,
    data: { name: string; responsible?: string; phone?: string; email?: string; notes?: string },
  ) => api.post(`/customers/${customerId}/sp-contractors`, data),
  updateContractor: (
    customerId: string,
    contractorId: string,
    data: { name?: string; responsible?: string; phone?: string; email?: string; notes?: string },
  ) => api.put(`/customers/${customerId}/sp-contractors/${contractorId}`, data),
  deleteContractor: (customerId: string, contractorId: string) =>
    api.delete(`/customers/${customerId}/sp-contractors/${contractorId}`),

  // Pets
  listPets: (customerId: string, unitId?: string) =>
    api.get(`/customers/${customerId}/pets${unitId ? `?unit_id=${unitId}` : ''}`).then(r => r.data),
  createPet: (customerId: string, data: { name: string; breed?: string; description?: string; unit_id?: string }) =>
    api.post(`/customers/${customerId}/pets`, data).then(r => r.data),
  updatePet: (customerId: string, petId: string, data: { name?: string; breed?: string; description?: string; unit_id?: string; species?: string; size?: string; weight?: number; sex?: string; color?: string; castrated?: boolean; vaccines?: string }) =>
    api.put(`/customers/${customerId}/pets/${petId}`, data).then(r => r.data),
  deletePet: (customerId: string, petId: string) =>
    api.delete(`/customers/${customerId}/pets/${petId}`).then(r => r.data),
  uploadPetPhoto: async (customerId: string, petId: string, file: File, slot: number = 1) => {
    const fd = new FormData();
    fd.append('photo', await normalizePhotoForUpload(file));
    return api.post(`/customers/${customerId}/pets/${petId}/photo?slot=${slot}`, fd, {
      headers: { 'Content-Type': null },
    }).then(r => r.data);
  },

  uploadCommonAreaPhoto: async (areaId: string, file: File, slot: number = 1) => {
    const fd = new FormData();
    fd.append('photo', await normalizePhotoForUpload(file));
    return api.post(`/common-areas/${areaId}/photo?slot=${slot}`, fd, {
      headers: { 'Content-Type': null },
    }).then(r => r.data);
  },

  deleteCommonAreaPhoto: (areaId: string, slot: number = 1) =>
    api.delete(`/common-areas/${areaId}/photo?slot=${slot}`).then(r => r.data),

  // Two-Factor Authentication (Email OTP via Keycloak)
  getTwoFactorStatus: () =>
    api.get('/user/profile/two-factor').then(r => r.data as { enabled: boolean }),
  toggleTwoFactor: (enabled: boolean) =>
    api.put('/user/profile/two-factor', { enabled }).then(r => r.data as { enabled: boolean }),

  // Keys (physical key management panel)
  listKeys: (customerId: string) =>
    api.get(`/customers/${customerId}/keys`).then(r => r.data),
  createKey: (customerId: string, data: { title: string; icon?: string; warning_message?: string; is_blocked?: boolean }) =>
    api.post(`/customers/${customerId}/keys`, data).then(r => r.data),
  updateKey: (customerId: string, keyId: string, data: { title?: string; icon?: string; warning_message?: string; is_blocked?: boolean }) =>
    api.put(`/customers/${customerId}/keys/${keyId}`, data).then(r => r.data),
  deleteKey: (customerId: string, keyId: string) =>
    api.delete(`/customers/${customerId}/keys/${keyId}`).then(r => r.data),
  takeKey: (customerId: string, keyId: string, data: { person_id?: string; person_name: string; notes?: string }) =>
    api.post(`/customers/${customerId}/keys/${keyId}/take`, data).then(r => r.data),
  returnKey: (customerId: string, keyId: string) =>
    api.post(`/customers/${customerId}/keys/${keyId}/return`, {}).then(r => r.data),
  getKeyHistory: (customerId: string, keyId: string) =>
    api.get(`/customers/${customerId}/keys/${keyId}/history`).then(r => r.data),

  // Unit Documents
  listUnitDocuments: (customerId: string, unitId: string) =>
    api.get(`/customers/${customerId}/units/${unitId}/documents`).then(r => r.data),
  uploadUnitDocument: (customerId: string, unitId: string, file: File, name?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name);
    return api.post(`/customers/${customerId}/units/${unitId}/documents`, fd, {
      headers: { 'Content-Type': null },
    }).then(r => r.data);
  },
  deleteUnitDocument: (customerId: string, unitId: string, docId: string) =>
    api.delete(`/customers/${customerId}/units/${unitId}/documents/${docId}`).then(r => r.data),

  // Department Documents
  listDepartmentDocuments: (customerId: string, departmentId: string) =>
    api.get(`/customers/${customerId}/departments/${departmentId}/documents`).then(r => r.data),
  uploadDepartmentDocument: (customerId: string, departmentId: string, file: File, name?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name);
    return api.post(`/customers/${customerId}/departments/${departmentId}/documents`, fd, {
      headers: { 'Content-Type': null },
    }).then(r => r.data);
  },
  deleteDepartmentDocument: (customerId: string, departmentId: string, docId: string) =>
    api.delete(`/customers/${customerId}/departments/${departmentId}/documents/${docId}`).then(r => r.data),

  // Time Zones
  listTimeZones: (customerId: string) =>
    api.get(`/customers/${customerId}/time-zones`).then(r => r.data),
  getTimeZone: (customerId: string, tzId: string) =>
    api.get(`/customers/${customerId}/time-zones/${tzId}`).then(r => r.data),
  createTimeZone: (customerId: string, data: Partial<TimeZone>) =>
    api.post(`/customers/${customerId}/time-zones`, data).then(r => r.data),
  updateTimeZone: (customerId: string, tzId: string, data: Partial<TimeZone>) =>
    api.put(`/customers/${customerId}/time-zones/${tzId}`, data).then(r => r.data),
  deleteTimeZone: (customerId: string, tzId: string) =>
    api.delete(`/customers/${customerId}/time-zones/${tzId}`).then(r => r.data),

  // Obras (Construction Works Module)
  listObras: (customerId: string, params?: { status?: string; unit_id?: string; search?: string }) =>
    api.get(`/customers/${customerId}/obras`, { params }).then(r => r.data),
  createObra: (customerId: string, data: Partial<Obra>) =>
    api.post(`/customers/${customerId}/obras`, data).then(r => r.data),
  getObra: (obraId: string) =>
    api.get(`/obras/${obraId}`).then(r => r.data),
  updateObra: (obraId: string, data: Partial<Obra>) =>
    api.put(`/obras/${obraId}`, data).then(r => r.data),
  deleteObra: (obraId: string) =>
    api.delete(`/obras/${obraId}`).then(r => r.data),
  uploadObraAttachment: (obraId: string, file: File, attachType: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', attachType);
    return api.post(`/obras/${obraId}/attachments`, fd, {
      headers: { 'Content-Type': null },
    }).then(r => r.data);
  },
  deleteObraAttachment: (obraId: string, index: number) =>
    api.delete(`/obras/${obraId}/attachments/${index}`).then(r => r.data),
};

// ============================================================================
// Device Session API (standalone functions using fetchWithAuth)
// ============================================================================

export interface UserDeviceSession {
  id: string;
  user_id: string;
  device_name: string;
  device_model: string;
  os_version: string;
  app_version: string;
  platform: string;
  is_active: boolean;
  is_current: boolean;
  last_active_at: string | null;
  logged_in_at: string | null;
  ip_address?: string;
  person_id?: string;
  person_name?: string;
}

/**
/** Extract a human-readable device name from a User-Agent string. */
function getCleanDeviceName(ua: string): string {
  // iOS native app sends clean names ("iPhone", "iPad") — not a UA string
  if (!ua.startsWith('Mozilla/')) return ua.substring(0, 100);

  // OS detection
  let os = 'Desconhecido';
  if (/iPhone/.test(ua)) os = 'iPhone';
  else if (/iPad/.test(ua)) os = 'iPad';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Macintosh|Mac OS X/.test(ua)) os = 'macOS';
  else if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';

  // Electron app
  const electronMatch = ua.match(/PantherAccess\/([\d.]+(?:-[\w.]+)?)/);
  if (electronMatch) return `${os} (PantherAccess)`;

  // Browser
  let browser = 'Browser';
  if (/CriOS/.test(ua)) browser = 'Chrome (iOS)';
  else if (/Chrome\/([\d.]+)/.test(ua)) browser = 'Chrome';
  else if (/Firefox\/([\d.]+)/.test(ua)) browser = 'Firefox';
  else if (/Safari\/([\d.]+)/.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';

  return `${os} — ${browser}`;
}

/**
 * Register or refresh a device session after login.
 * Returns the backend-assigned device UUID, or null on failure.
 */
export async function registerWebDevice(
  platform: 'web' | 'electron' = 'web',
): Promise<string | null> {
  try {
    const isElectron = typeof window !== 'undefined' && !!(window as any).electronAPI?.isElectron?.();
    const resolvedPlatform = isElectron ? 'electron' : platform;

    const r = await fetchWithAuth(`${getApiBaseUrl()}/api/v1/user-devices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: resolvedPlatform,
        app_version: process.env.REACT_APP_VERSION ?? 'unknown',
        device_name: getCleanDeviceName(navigator.userAgent),
      }),
    });
    if (!r.ok) return null;
    const data = await r.json();
    const id = data?.id ?? null;
    if (id) sessionStorage.setItem('panther_device_id', id);
    return id;
  } catch {
    return null;
  }
}

/**
 * List all active sessions for the current user.
 */
export async function listMyDeviceSessions(): Promise<UserDeviceSession[]> {
  try {
    const currentId = sessionStorage.getItem('panther_device_id') ?? '';
    const url = `${getApiBaseUrl()}/api/v1/user-devices${currentId ? `?current_device_id=${currentId}` : ''}`;
    const r = await fetchWithAuth(url);
    if (!r.ok) return [];
    const data = await r.json();
    return data?.data ?? [];
  } catch {
    return [];
  }
}

/**
 * Revoke a specific device session.
 */
export async function revokeDeviceSession(deviceId: string): Promise<boolean> {
  try {
    const r = await fetchWithAuth(`${getApiBaseUrl()}/api/v1/user-devices/${deviceId}`, {
      method: 'DELETE',
    });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * List all active device sessions for a customer (admin view).
 */
export async function listCustomerDeviceSessions(
  customerId: string,
): Promise<UserDeviceSession[]> {
  try {
    const r = await fetchWithAuth(
      `${getApiBaseUrl()}/api/v1/customers/${customerId}/device-sessions`,
    );
    if (!r.ok) return [];
    const data = await r.json();
    return data?.data ?? [];
  } catch {
    return [];
  }
}

export default api;
